import type {AgentInfo} from '../types/session'

/**
 * Regex pattern to match agent mentions in the format @agent-name[agent] or @copilot
 */
export const AGENT_MENTION_REGEX = /(@[a-zA-Z0-9_-]+\[agent\]|@copilot)/gi

/**
 * Information about a matched agent mention
 */
export interface AgentMentionMatch {
  /** The full matched text (e.g., "@copilot" or "@agent-name[agent]") */
  mentionText: string
  /** Whether this mention matches the current agent */
  isCurrentAgent: boolean
  /** Type of mention */
  type: 'copilot' | 'agent' | 'unknown'
}

/**
 * Checks if a mention matches the current agent
 * @param mentionText The full mention text (e.g., "@copilot" or "@agent-name[agent]")
 * @param agentInfo The current agent information
 * @returns Information about the mention match
 */
export function matchAgentMention(mentionText: string, agentInfo?: AgentInfo): AgentMentionMatch {
  const lowerMention = mentionText.toLowerCase()

  // Handle @copilot mentions
  if (lowerMention === '@copilot') {
    const isCurrentAgent = agentInfo?.display_name?.toLowerCase() === 'copilot'
    return {
      mentionText,
      isCurrentAgent: isCurrentAgent ?? false,
      type: 'copilot',
    }
  }

  // Handle @agent-name[agent] mentions
  if (lowerMention.endsWith('[agent]')) {
    const mentionedAgent = mentionText.slice(1, -7).toLowerCase() // Remove @ and [agent]

    if (agentInfo?.display_name) {
      const agentDisplayName = agentInfo.display_name.toLowerCase().replace(/\s+/g, '-')
      const isCurrentAgent = mentionedAgent === agentDisplayName

      return {
        mentionText,
        isCurrentAgent,
        type: 'agent',
      }
    }

    return {
      mentionText,
      isCurrentAgent: false,
      type: 'agent',
    }
  }

  return {
    mentionText,
    isCurrentAgent: false,
    type: 'unknown',
  }
}

export const createMarkdownWithAgentLinks = (body: string, agentInfo?: AgentInfo): string => {
  const result = body.replace(AGENT_MENTION_REGEX, match => {
    const mentionMatch = matchAgentMention(match, agentInfo)

    if (mentionMatch.isCurrentAgent) {
      if (mentionMatch.type === 'copilot') {
        return `[${match}](https://gh.io/copilot-coding-agent-docs)`
      } else if (mentionMatch.type === 'agent' && agentInfo) {
        return `[${match}](${agentInfo.doc_url})`
      }
    }

    return match
  })

  return result
}
